import type { PswapOrder } from '@flipster/r230522/model/prex.v2.r230522.tables.private_orders';
import type { StepModel } from '@flipster/react/misc/step/use-steps';

import type { TpslModalSteps } from '.';
import type { PositionType } from '../../shared/tpsl/bunja';

export type TpslTab = 'entire' | 'partial';
export type HasTpslOrder = 'empty' | 'single' | 'multiple';

/**
 * 모달을 열 때 보여줄 탭 결정.
 *
 * - Flow B(기존 주문 수정): `modifyTargetType`(수정 대상 주문의 종류)이 주어지면 그 탭으로 **최우선** 고정한다
 *   (entire 주문→entire, partial 주문→partial). undefined면 수정이 아님(생성).
 * - Flow A(차트 드래그 생성): **항상 entire 탭이 기본**이다. entire가 이미 있으면 그 entire가
 *   수정되고(entire-update), partial은 사용자가 partial 탭으로 전환해 새로 생성한다.
 * - 그 외(드래그 아님/일반 모달 오픈): 주문 없음·entire 존재 → entire, partial만 존재 → partial.
 */
export function resolveTpslTab(params: {
  positionType: PositionType;
  hasTpslOrder: HasTpslOrder;
  hasEntireOrder: boolean;
  openedViaDrag: boolean;
  /** Flow B: 수정 대상 주문의 종류(entire/partial). 있으면 그 탭으로 최우선 고정, undefined면 생성. */
  modifyTargetType?: TpslTab;
}): TpslTab {
  const { positionType, hasTpslOrder, hasEntireOrder, openedViaDrag, modifyTargetType } = params;
  if (modifyTargetType) return modifyTargetType;
  if (positionType !== 'open') return 'entire';
  if (openedViaDrag) return 'entire'; // Flow A 생성: 항상 entire 탭 기본
  if (hasTpslOrder === 'empty') return 'entire';
  return hasEntireOrder ? 'entire' : 'partial';
}

/**
 * 기존 TP/SL order line을 끌어 "수정"으로 모달을 열 때, 그 주문이 entire인지 partial인지와
 * 대상 주문을 찾는다(드래그된 `orderId` 기준). 찾으면 해당 탭으로 update 진입한다.
 * - `entire`(qty 0) 목록에 있으면 → entire 수정
 * - `partial`(qty>0) 목록에 있으면 → 그 주문 partial 수정
 * `modifyOrderId`가 없거나(=생성 드래그) 주문을 못 찾으면 undefined → 생성 로직으로 폴백.
 */
export function resolveTpslModifyTarget<Order extends { orderId: string }>(params: {
  modifyOrderId: string | undefined;
  entireOrders: readonly Order[];
  partialOrders: readonly Order[];
}): { tab: TpslTab; order: Order } | undefined {
  const { modifyOrderId, entireOrders, partialOrders } = params;
  if (!modifyOrderId) return undefined;
  const entire = entireOrders.find((order) => order.orderId === modifyOrderId);
  if (entire) return { tab: 'entire', order: entire };
  const partial = partialOrders.find((order) => order.orderId === modifyOrderId);
  if (partial) return { tab: 'partial', order: partial };
  return undefined;
}

/**
 * 모달이 처음 진입할 step(+context)을 결정한다. tab/modifyTarget(순수 결정) + 현재 주문 상태를 받아
 * add/update/manage 스텝을 고른다. atom은 이 함수에 scope 값만 모아 넘기는 얇은 어댑터다.
 * - Flow B(수정 대상 있음): entire→entire-update / partial→partial-update (그 주문).
 * - open + partial 탭: partial-manage(목록). open + entire 탭: 주문 없음→entire-add / entire 있음→entire-update.
 * - pending: 주문 없음→entire-add / 있으면 그 주문 entire-update(UI상 entire지만 실제 100% partial).
 */
export function resolveInitialStep(params: {
  positionType: PositionType;
  tpslTab: TpslTab;
  hasTpslOrder: HasTpslOrder;
  modifyTarget: { tab: TpslTab; order: PswapOrder } | undefined;
  /** open + entire 탭에서 수정 대상이 될 기존 entire 주문(없으면 entire-add). */
  entireOrder: PswapOrder | undefined;
  /** pending에서 수정 대상이 될 주문(첫 partial order). */
  pendingSelectedOrder: PswapOrder | undefined;
}): StepModel<TpslModalSteps> {
  const { positionType, tpslTab, hasTpslOrder, modifyTarget, entireOrder, pendingSelectedOrder } = params;

  if (modifyTarget) {
    return modifyTarget.tab === 'entire'
      ? { step: 'entire-update', context: { tpslOrder: modifyTarget.order } }
      : { step: 'partial-update', context: { tpslOrder: modifyTarget.order } };
  }

  switch (positionType) {
    case 'open': {
      // 생성 드래그는 entire 탭이 기본이라 partial 탭엔 비-드래그(partial만 존재) 케이스만 도달 → 목록.
      if (tpslTab === 'partial') return { step: 'partial-manage', context: undefined };
      if (hasTpslOrder === 'empty') return { step: 'entire-add', context: undefined };
      if (entireOrder) return { step: 'entire-update', context: { tpslOrder: entireOrder } };
      return { step: 'entire-add', context: undefined };
    }
    case 'pending': {
      if (hasTpslOrder === 'empty') return { step: 'entire-add', context: undefined };
      if (pendingSelectedOrder) return { step: 'entire-update', context: { tpslOrder: pendingSelectedOrder } };
      return { step: 'entire-add', context: undefined };
    }
    default:
      throw new Error('Unexpected Position Type');
  }
}
